// src/services/scannerApiService.ts

/**
 * Scanner API Service
 * Handles communication with the backend scanner API
 */
import { auth } from '@/lib/firebase';
import { toast } from '@/components/ui/use-toast';
import { scanQuotaService } from './scanQuotaService';

// Backend URL
const SCANNER_API_URL = "https://us-central1-pokescan2.cloudfunctions.net/scanCard";

export interface ScanResult {
  success: boolean;
  cardId?: string;
  error?: string;
  remainingScans?: number;
}

export const scannerApiService = {
  /**
   * Check if user can scan (has remaining scans)
   */
  async checkScanAvailability() {
    try {
      const quota = await scanQuotaService.getQuota();
      return {
        remainingScans: quota.remainingScans,
        isSubscribed: quota.isSubscribed,
        subscriptionTier: quota.subscriptionTier
      };
    } catch (error) {
      console.error('Error checking scan availability:', error);
      // Return conservative default in case of error
      return {
        remainingScans: 0,
        isSubscribed: false
      };
    }
  },
  
  /**
   * Identify a card from an image using the backend API
   */
  async identifyCard(imageBlob: Blob, progressCallback?: (progress: number) => void): Promise<ScanResult> {
    try {
      // First verify the user has scans available
      const hasScans = await scanQuotaService.hasAvailableScans();
      if (!hasScans) {
        toast({
          variant: "destructive",
          title: "No Scans Remaining",
          description: "Subscribe to get more scans or upgrade to Pro for unlimited scanning."
        });
        return {
          success: false,
          error: "No scans remaining. Subscribe for more scans."
        };
      }
      
      // Create form data with the image
      const formData = new FormData();
      formData.append("file", imageBlob);
      
      // Add auth header if user is logged in
      let headers: Record<string,string> = {};
      if (auth.currentUser) {
        try {
          const idToken = await auth.currentUser.getIdToken(true); // Force token refresh
          headers = { Authorization: `Bearer ${idToken}` };
        } catch (error) {
          console.error("Error getting auth token:", error);
          // Continue without token if we can't get one
        }
      }
      
      // Simulate progress for better UX
      let progressInterval: NodeJS.Timeout | null = null;
      if (progressCallback) {
        let progress = 0;
        progressCallback(progress);
        
        progressInterval = setInterval(() => {
          // Simulate progress up to 90% (remaining will be filled when we get response)
          progress = Math.min(90, progress + Math.random() * 10);
          progressCallback(progress);
        }, 300);
      }
      
      console.log("Sending scan request to API...");
      
      // Send the image to the backend
      const response = await fetch(SCANNER_API_URL, {
        method: "POST",
        body: formData,
        headers
      });
      
      // Clear progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      // 🐞 DEBUG: always capture and log the raw backend response
      const rawText = await response.clone().text();
      console.log("🐞 Raw scanner-backend response:", rawText);
      
      // Parse JSON if possible
      let result: any = {};
      try {
        result = JSON.parse(rawText);
      } catch (e) {
        console.warn("Backend did not return valid JSON:", e);
      }
      
      // Invalidate quota cache after scan
      scanQuotaService.invalidateCache();
      
      // Complete progress
      if (progressCallback) {
        progressCallback(100);
      }
      
      // Handle different response statuses
      if (response.ok) {
        return {
          success: true,
          cardId: result.cardId,
          remainingScans: result.remainingScans
        };
      } else if (response.status === 429) {
        toast({
          variant: "destructive", 
          title: "Scan Limit Reached",
          description: "You've reached your scan limit. Subscribe for more scans."
        });
        return {
          success: false,
          error: "You've reached your scan limit."
        };
      } else if (response.status === 404) {
        return {
          success: false,
          error: "We couldn't identify this card. Please try scanning again with better lighting."
        };
      } else {
        return {
          success: false,
          error: result.error || "An unexpected error occurred."
        };
      }
    } catch (error: any) {
      console.error("Scanner API error:", error);
      return {
        success: false,
        error: "Network error. Please check your connection and try again."
      };
    }
  }
};

export default scannerApiService;
